import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { applyPlans, comparePlans, defaultChoice, planFrom, problemText, readApply, saveMatches, type AppliedPlan, type ComparedPlan, type PlanChoice } from "../../api/migrate";
import type { PlanRow } from "../../domain/migrationSplit";
import { formatInr } from "../../domain/tiers";

const POLL_MS = 2000;
const POLLS = 90;
const wait = (ms: number) => new Promise((done) => setTimeout(done, ms));
const rupees = (p?: number | null) => (p === undefined || p === null ? "not known" : formatInr(p));
const LABEL = { same: "Same plan exists", price_differs: "Same speed and days, different price or logins", new: "No such plan yet" };

/** The review before any customer: which plan each name in the file means. Nothing is written until Apply. */
export function MigratePlans({ rows }: { rows: PlanRow[] }) {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const [compared, setCompared] = useState<ComparedPlan[] | null>(null);
  const [choices, setChoices] = useState<Record<string, PlanChoice>>({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [applied, setApplied] = useState<AppliedPlan[] | null>(null);
  const [saved, setSaved] = useState(0);

  const compare = async () => {
    setBusy("Comparing");
    setError("");
    setApplied(null);
    try {
      const c = await comparePlans(identity, ns, rows);
      setCompared(c);
      setChoices(Object.fromEntries(c.map((x) => [x.source_name, defaultChoice(x)])));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not compare the plans.");
    } finally {
      setBusy("");
    }
  };

  const apply = async () => {
    const plan = planFrom(rows, choices);
    setBusy("Applying");
    setError("");
    try {
      if (plan.matches.length) setSaved((await saveMatches(identity, ns, plan.matches)).data.saved);
      if (plan.rows.length) {
        const id = await applyPlans(identity, ns, plan.rows);
        for (let i = 0; i < POLLS; i++) {
          await wait(POLL_MS);
          const job = await readApply(identity, ns, id);
          if (job.status === "done") {
            setApplied(job.rows ?? []);
            return;
          }
          if (job.status === "failed") throw new Error(job.error ?? "The plans could not be created.");
        }
        throw new Error("This is taking too long. Compare again to see what was created.");
      }
      setApplied([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply.");
    } finally {
      setBusy("");
    }
  };

  const open = compared?.filter((c) => !c.problem && !choices[c.source_name]).length ?? 0;
  const failed = applied?.filter((a) => a.error) ?? [];
  return (
    <Paper sx={{ p: 2 }} data-testid="migrate-plans">
      <Typography variant="subtitle1">Match your plans</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Your customers keep what they pay today. Existing plans are never changed. A special plan keeps the old price, is not for sale, and customers stay on it until you move them.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
        <Button variant="outlined" disabled={Boolean(busy)} onClick={() => void compare()}>Compare with my plans</Button>
        {compared && <Button variant="contained" disabled={Boolean(busy) || open > 0} onClick={() => void apply()}>Apply</Button>}
        {busy && <Typography variant="body2">{busy}</Typography>}
        {open > 0 && <Typography variant="body2">Choose for {open} more</Typography>}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {applied && failed.length === 0 && <Alert severity="success" sx={{ mb: 1 }}>Plans ready: {saved} matched, {applied.length} created. Now run the trial below.</Alert>}
      {failed.map((f) => <Alert key={f.source_name} severity="error" sx={{ mb: 1 }}>{f.source_name}: {f.error}</Alert>)}
      {compared && (
        <Table size="small">
          <TableHead><TableRow><TableCell>Plan in your file</TableCell><TableCell>Logins</TableCell><TableCell>Price</TableCell><TableCell>Found</TableCell><TableCell>Do this</TableCell></TableRow></TableHead>
          <TableBody>
            {compared.map((c) => (
              <TableRow key={c.source_name}>
                <TableCell>{c.source_name}</TableCell>
                <TableCell>{c.devices ?? ""}</TableCell>
                <TableCell>{c.price === undefined ? "" : rupees(c.price)}</TableCell>
                <TableCell>{c.problem ? problemText(c.problem) : c.match ? LABEL[c.match] : ""}</TableCell>
                <TableCell>
                  {!c.problem && (
                    <TextField select size="small" fullWidth value={choices[c.source_name] ?? ""} onChange={(e) => setChoices((o) => ({ ...o, [c.source_name]: e.target.value }))}>
                      {(c.existing ?? []).map((x) => <MenuItem key={x.groupname} value={`use:${x.groupname}`}>Use {x.groupname} at {rupees(x.price)}</MenuItem>)}
                      {c.match === "price_differs" && <MenuItem value="special">Special plan at the old price, not for sale</MenuItem>}
                      {c.match === "new" && <MenuItem value="new-off">New plan, not for sale</MenuItem>}
                      {c.match === "new" && <MenuItem value="new-on">New plan, on sale</MenuItem>}
                    </TextField>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
